import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  LOCALE_ID,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDa from '@angular/common/locales/da';

registerLocaleData(localeDa);
import { AuthenticationService } from '@core/authentication/authentication.service';
import { SiteStore } from '@core/site/site.store';
import {
  provideRouter,
  TitleStrategy,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { AbookTitleStrategy } from '@core/seo/abook-title.strategy';
import { skipQueryOnlyTransitions } from '@core/routing/view-transitions';
import { PageTitle } from '@abook-ui/components/page/page-title';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
  withNoIncrementalHydration,
} from '@angular/platform-browser';
import {
  AbstractSecurityStorage,
  DefaultLocalStorageService,
  LogLevel,
  provideAuth,
  StsConfigLoader,
  StsConfigStaticLoader,
} from 'angular-auth-oidc-client';

// document.baseURI resolves <base href> to an absolute URL. With base href "/" it equals
// window.location.origin; unlike the origin, it still points at the app when served from a sub-path.
const origin =
  typeof window !== 'undefined' ? document.baseURI.replace(/\/$/, '') : 'https://localhost:4202';

import { routes } from './app.routes';
import { revalidatingTransferInterceptor } from '@core/http-interceptors/revalidating-transfer.interceptor';
import { authInterceptor } from '@core/http-interceptors/auth.interceptor';
import { API_URL } from '@core/tokens/api-url.token';
import { RESOLVED_TENANT } from '@core/tokens/resolved-tenant.token';
import { provideIconFontSet } from '@abook-ui/utils/provide-icon-font-set';

export const appConfig: ApplicationConfig = {
  providers: [
    provideIconFontSet(),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions({ onViewTransitionCreated: skipQueryOnlyTransitions }),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    ),
    // The revalidating transfer cache first (#999): it replays the static page's responses at the
    // first view and sends the refresh through authInterceptor, so a signed-in user gets their own.
    provideHttpClient(withFetch(), withInterceptors([revalidatingTransferInterceptor, authInterceptor])),
    provideClientHydration(
      withEventReplay(),
      withNoIncrementalHydration(),
      // Off in every build: the revalidating interceptor replaces Angular's own transfer cache,
      // which would replay the same responses without ever refreshing them (#999).
      withNoHttpTransferCache(),
    ),

    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: () => {
          const { authServer } = inject(RESOLVED_TENANT);
          return new StsConfigStaticLoader({
            authority: `${authServer}/.well-known/openid-configuration`,
            redirectUrl: origin,
            postLogoutRedirectUri: origin,
            // GitHub Pages redirects the slash-less redirect_uri
            // (.../pr-N/customer) to .../pr-N/customer/ with a 301. The library
            // otherwise compares the landing URL against redirectUrl as exact
            // strings before treating ?code= as a callback, so on a sub-path
            // base href the code is never exchanged and login loops. The
            // redirect_uri has to stay slash-less: that is the form registered
            // on the auth server (RedirectUris).
            // State, nonce and PKCE are still validated.
            checkRedirectUrlWhenCheckingIfIsCallback: false,
            clientId: 'abook.www.v3.client',
            scope: 'openid profile email abook.api.v3.scope.access offline_access',
            responseType: 'code',
            silentRenew: true,
            useRefreshToken: true,
            renewUserInfoAfterTokenRenew: true,
            unauthorizedRoute: '/authentication/unauthorized',
            logLevel: LogLevel.Warn,
          });
        },
      },
    }),

    {
      provide: API_URL,
      useFactory: () => inject(RESOLVED_TENANT).apiUrl,
    },
    { provide: LOCALE_ID, useValue: 'da' },
    { provide: TitleStrategy, useClass: AbookTitleStrategy },
    // The same instance: abook-page sets a detail page's name through it (#690).
    { provide: PageTitle, useExisting: TitleStrategy },

    provideAppInitializer(() => inject(SiteStore).load()),
    provideAppInitializer(() => inject(AuthenticationService).checkAuth()),

    {
      provide: AbstractSecurityStorage,
      useClass: DefaultLocalStorageService,
    },
  ],
};
